import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { cashFlowService } from '../../../services/financeService';
import { CashFlowProjection, CashFlowProjectionItem } from '../../../types/finance';
import { toast } from 'react-hot-toast';
import { FaPlus, FaTrash, FaSave } from 'react-icons/fa';

const CashFlowTab: React.FC = () => {
  const { user } = useAuth();
  const [projections, setProjections] = useState<CashFlowProjection[]>([]);
  const [selectedProjection, setSelectedProjection] = useState<CashFlowProjection | null>(null);
  const [projectionItems, setProjectionItems] = useState<CashFlowProjectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [initialBalance, setInitialBalance] = useState(0);
  const [newItem, setNewItem] = useState<Partial<CashFlowProjectionItem>>({
    date: '',
    description: '',
    amount: 0,
    type: 'income',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const projectionsData = await cashFlowService.getAll();
        setProjections(projectionsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load cash flow data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSelectProjection = async (id: string) => {
    try {
      const projection = await cashFlowService.getById(id);
      setSelectedProjection(projection);
      setName(projection.name);
      setStartDate(projection.start_date);
      setEndDate(projection.end_date);
      setInitialBalance(projection.initial_balance);
      setProjectionItems(projection.items || []);
    } catch (error) {
      console.error('Error fetching projection:', error);
      toast.error('Failed to load projection details');
    }
  };

  const handleCreateProjection = async () => {
    if (!user || !name || !startDate || !endDate) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const totalIncome = projectionItems
        .filter(item => item.type === 'income')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const totalExpenses = projectionItems
        .filter(item => item.type === 'expense')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const finalBalance = initialBalance + totalIncome - totalExpenses;
      
      const newProjection: CashFlowProjection = {
        user_id: user.id,
        name,
        start_date: startDate,
        end_date: endDate,
        initial_balance: initialBalance,
        final_balance: finalBalance
      };
      
      const savedProjection = await cashFlowService.create(newProjection, projectionItems);
      setProjections([savedProjection, ...projections]);
      setSelectedProjection(savedProjection);
      toast.success('Projection created');
    } catch (error) {
      console.error('Error creating projection:', error);
      toast.error('Failed to create projection');
    }
  };

  const handleUpdateProjection = async () => {
    if (!selectedProjection || !user) return;
    
    try {
      const totalIncome = projectionItems
        .filter(item => item.type === 'income')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const totalExpenses = projectionItems
        .filter(item => item.type === 'expense')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const finalBalance = initialBalance + totalIncome - totalExpenses;
      
      const updatedProjection: Partial<CashFlowProjection> = {
        name,
        start_date: startDate,
        end_date: endDate,
        initial_balance: initialBalance,
        final_balance: finalBalance
      };
      
      const savedProjection = await cashFlowService.update(
        selectedProjection.id!,
        updatedProjection,
        projectionItems
      );
      
      setProjections(projections.map(p => 
        p.id === savedProjection.id ? savedProjection : p
      ));
      setSelectedProjection(savedProjection);
      toast.success('Projection updated');
    } catch (error) {
      console.error('Error updating projection:', error);
      toast.error('Failed to update projection');
    }
  };

  const handleDeleteProjection = async () => {
    if (!selectedProjection) return;
    
    try {
      await cashFlowService.delete(selectedProjection.id!);
      setProjections(projections.filter(p => p.id !== selectedProjection.id));
      setSelectedProjection(null);
      setProjectionItems([]);
      resetForm();
      toast.success('Projection deleted');
    } catch (error) {
      console.error('Error deleting projection:', error);
      toast.error('Failed to delete projection');
    }
  };

  const handleAddItem = () => {
    if (!newItem.date || !newItem.description || !newItem.amount) {
      toast.error('Please fill in all item fields');
      return;
    }
    
    const item: CashFlowProjectionItem = {
      projection_id: selectedProjection?.id || '',
      date: newItem.date!,
      description: newItem.description!,
      amount: Number(newItem.amount),
      type: newItem.type as 'income' | 'expense',
      is_recurring: false
    };
    
    setProjectionItems([...projectionItems, item]);
    setNewItem({
      date: '',
      description: '',
      amount: 0,
      type: 'income',
    });
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...projectionItems];
    updatedItems.splice(index, 1);
    setProjectionItems(updatedItems);
  };

  const handleNewProjection = () => {
    setSelectedProjection(null);
    setProjectionItems([]);
    resetForm();
  };

  const resetForm = () => {
    setName('');
    setStartDate('');
    setEndDate('');
    setInitialBalance(0);
    setNewItem({
      date: '',
      description: '',
      amount: 0,
      type: 'income',
    });
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading projections...</div>;
  }

  const totalIncome = projectionItems
    .filter(item => item.type === 'income')
    .reduce((sum, item) => sum + Number(item.amount), 0);
  
  const totalExpenses = projectionItems
    .filter(item => item.type === 'expense')
    .reduce((sum, item) => sum + Number(item.amount), 0);
  
  const finalBalance = initialBalance + totalIncome - totalExpenses;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Sidebar - Projection List */}
      <div className="lg:col-span-1">
        <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Your Projections</h3>
            <button
              onClick={handleNewProjection}
              className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white transition hover:bg-primary/90"
            >
              <FaPlus className="text-xs" /> New
            </button>
          </div>
          
          {projections.length === 0 ? (
            <p className="text-sm text-gray-500">No projections yet</p>
          ) : (
            <ul className="space-y-2">
              {projections.map(projection => (
                <li 
                  key={projection.id} 
                  className={`cursor-pointer rounded-lg p-3 transition-all ${
                    selectedProjection?.id === projection.id 
                      ? 'border-l-4 border-primary bg-blue-50 shadow-sm' 
                      : 'hover:bg-white'
                  }`}
                  onClick={() => handleSelectProjection(projection.id!)}
                >
                  <div className="text-sm font-medium text-gray-900">{projection.name}</div>
                  <div className="mt-1 text-xs text-gray-600">
                    {new Date(projection.start_date).toLocaleDateString()} to {new Date(projection.end_date).toLocaleDateString()}
                  </div>
                  <div className="mt-2 flex justify-between border-t border-gray-200 pt-2">
                    <span className="text-xs text-gray-600">Final Balance:</span>
                    <span className={`text-xs font-bold ${
                      projection.final_balance >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${projection.final_balance.toFixed(2)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:col-span-2">
        <div className="space-y-6">
          {/* Projection Info */}
          <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
            <h3 className="mb-4 text-base font-semibold text-gray-900">
              {selectedProjection ? 'Edit Projection' : 'New Projection'}
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Q1 2025 Forecast"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Initial Balance ($) *</label>
                <input
                  type="number"
                  value={initialBalance}
                  onChange={(e) => setInitialBalance(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="1000.00"
                  step="0.01"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">End Date *</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>

          {/* Add Items */}
          <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
            <h4 className="mb-4 text-base font-semibold text-gray-900">Add Transaction</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
                <input
                  type="date"
                  value={newItem.date || ''}
                  onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                <select
                  value={newItem.type}
                  onChange={(e) => setNewItem({ ...newItem, type: e.target.value as 'income' | 'expense' })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <input
                  type="text"
                  value={newItem.description || ''}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Client payment"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Amount ($)</label>
                <input
                  type="number"
                  value={newItem.amount || ''}
                  onChange={(e) => setNewItem({ ...newItem, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="500.00"
                  step="0.01"
                />
              </div>
            </div>
            <button
              onClick={handleAddItem}
              className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
            >
              <FaPlus /> Add Item
            </button>
          </div>

          {/* Items List */}
          {projectionItems.length > 0 && (
            <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
              <h4 className="mb-4 text-base font-semibold text-gray-900">Transactions</h4>
              <div className="space-y-2">
                {projectionItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.description}</div>
                      <div className="text-xs text-gray-600">
                        {new Date(item.date).toLocaleDateString()} • {item.type}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${
                        item.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.type === 'income' ? '+' : '-'}${item.amount.toFixed(2)}
                      </span>
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash className="text-xs" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary & Actions */}
          {name && startDate && endDate && (
            <div className="rounded-xl bg-blue-50 p-5 shadow-sm ring-1 ring-blue-200">
              <h4 className="mb-3 text-base font-semibold text-blue-900">Summary</h4>
              <div className="grid gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Initial Balance:</span>
                  <span className="font-medium">${initialBalance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Total Income:</span>
                  <span className="font-medium text-green-600">+${totalIncome.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-700">Total Expenses:</span>
                  <span className="font-medium text-red-600">-${totalExpenses.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-blue-200 pt-2">
                  <span className="font-semibold text-blue-900">Final Balance:</span>
                  <span className={`text-lg font-bold ${
                    finalBalance >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ${finalBalance.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                {selectedProjection && (
                  <button
                    onClick={handleDeleteProjection}
                    className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                  >
                    <FaTrash /> Delete
                  </button>
                )}
                <button
                  onClick={selectedProjection ? handleUpdateProjection : handleCreateProjection}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
                >
                  <FaSave /> {selectedProjection ? 'Update' : 'Save'} Projection
                </button>
              </div>
            </div>
          )}

          {projections.length === 0 && !selectedProjection && (
            <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
              <div className="text-4xl mb-4">💸</div>
              <h3 className="text-lg font-semibold text-gray-900">No Cash Flow Projections Yet</h3>
              <p className="mt-2 text-sm text-gray-600">
                Create your first projection to forecast your cash flow for 3-6 months
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashFlowTab;
